import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Button } from '../ui';
import { useScrollReveal } from '../../hooks';
import './Newsletter.css';

/**
 * Newsletter subscription component
 * Email signup with validation and success state
 */
const Newsletter: React.FC = () => {
	const { t } = useTranslation();
	const { ref, isVisible } = useScrollReveal({ threshold: 0.2 });
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
	const [message, setMessage] = useState('');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			setStatus('error');
			setMessage(t('newsletter.invalidEmail', 'Please enter a valid email address'));
			return;
		}

		setStatus('loading');

		// Simulate API call (replace with actual endpoint)
		try {
			await new Promise((resolve) => setTimeout(resolve, 1000));
			setStatus('success');
			setMessage(t('newsletter.success', 'Thank you for subscribing! Check your inbox for exclusive offers.'));
			setEmail('');
		} catch {
			setStatus('error');
			setMessage(t('newsletter.error', 'Something went wrong. Please try again.'));
		}
	};

	return (
		<section
			className="newsletter"
			ref={ref as React.RefObject<HTMLElement>}
		>
			<div className={`newsletter-container ${isVisible ? 'visible' : ''}`}>
				<div className="newsletter-content">
					<div className="newsletter-icon">📧</div>
					<h2>{t('newsletter.title', 'Get Exclusive Offers')}</h2>
					<p>{t('newsletter.subtitle', 'Subscribe to our newsletter for special deals and party tips!')}</p>
				</div>

				<form className="newsletter-form" onSubmit={handleSubmit}>
					{status !== 'success' ? (
						<>
							<div className="newsletter-input-group">
								<Input
									type="email"
									placeholder={t('newsletter.placeholder', 'Enter your email address')}
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										if (status === 'error') setStatus('idle');
									}}
									disabled={status === 'loading'}
								/>
								<Button
									type="submit"
									variant="primary"
									disabled={status === 'loading'}
								>
									{status === 'loading' ? (
										<span className="loading-spinner"></span>
									) : (
										t('newsletter.subscribe', 'Subscribe')
									)}
								</Button>
							</div>
							{status === 'error' && (
								<p className="newsletter-error">{message}</p>
							)}
							<p className="newsletter-privacy">
								{t('newsletter.privacy', 'We respect your privacy. Unsubscribe anytime.')}
							</p>
						</>
					) : (
						<div className="newsletter-success">
							<span className="success-icon">✓</span>
							<p>{message}</p>
						</div>
					)}
				</form>

				<div className="newsletter-benefits">
					<div className="benefit">
						<span>🎁</span>
						<span>{t('newsletter.benefit1', 'Exclusive discounts')}</span>
					</div>
					<div className="benefit">
						<span>📅</span>
						<span>{t('newsletter.benefit2', 'Party planning tips')}</span>
					</div>
					<div className="benefit">
						<span>🍳</span>
						<span>{t('newsletter.benefit3', 'New menu updates')}</span>
					</div>
				</div>
			</div>
		</section>
	);
};

export default Newsletter;




